import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { SelectButton } from 'primereact/selectbutton';
import { Avatar } from 'primereact/avatar';
import { TabMenu } from 'primereact/tabmenu';
import { Menu } from 'primereact/menu';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Paginator } from 'primereact/paginator';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import lendingNetworkService from '../../services/admin/lendingNetworkService';

/**
 * Per-row kebab menu for member actions.
 * One Menu instance per row, model pre-bound to rowData via closure.
 */
const RowActionsMenu = ({ rowData, actioning, onApprove, onDeny, onRemove }) => {
    const menuRef = useRef(null);

    const items = useMemo(() => {
        const list = [];
        if (rowData.lending_network_status !== 'approved') {
            list.push({ label: 'Approve', icon: 'pi pi-check', command: () => onApprove(rowData) });
        }
        if (rowData.lending_network_status !== 'denied') {
            list.push({ label: 'Deny', icon: 'pi pi-times', command: () => onDeny(rowData) });
        }
        if (list.length > 0) list.push({ separator: true });
        list.push({ label: 'Remove from network', icon: 'pi pi-trash', command: () => onRemove(rowData) });
        return list;
    }, [rowData, onApprove, onDeny, onRemove]);

    return (
        <>
            <Menu
                model={items}
                popup
                ref={menuRef}
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
            />
            <Button
                icon="pi pi-ellipsis-v"
                rounded
                text
                size="small"
                aria-label="Member actions"
                disabled={actioning}
                onClick={(e) => menuRef.current?.toggle(e)}
            />
        </>
    );
};

const STATUS_SEVERITY = { pending: 'warning', approved: 'success', denied: 'danger' };
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', denied: 'Denied' };

/**
 * Header content for AccordionTab in the mobile member list.
 * Accordion handles the chevron + toggle + a11y — we only supply avatar, name,
 * status tag, and kebab menu.
 */
const MemberHeader = ({ member }) => {
    const status = member.lending_network_status;
    return (
        <div className="flex justify-content-between align-items-center gap-2 w-full">
            <span className="flex-1 min-w-0 font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                {member.full_name || '—'}
            </span>
            <Tag
                value={STATUS_LABEL[status] || status || '—'}
                severity={STATUS_SEVERITY[status] || 'info'}
                className="text-xs flex-shrink-0"
            />
        </div>
    );
};

/**
 * Expanded content shown when AccordionTab is open.
 * Mirrors every column of the desktop DataTable, with "—" for empty values.
 */
const MemberDetails = ({ member, actioning, onApprove, onDeny, onRemove }) => {
    const status = member.lending_network_status;
    const actionAt = member.lending_network_approved_at;
    const dateText = actionAt && status !== 'pending'
        ? `${new Date(actionAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${new Date(actionAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`
        : '—';
    const dateIcon = status === 'approved' ? 'pi-check-circle text-green-600'
        : status === 'denied' ? 'pi-times-circle text-red-500'
        : 'pi-clock text-500';

    return (
        <div className="flex flex-column gap-2 text-xs">
            <div className="flex align-items-center gap-2">
                <i className="pi pi-envelope text-500 w-1rem" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Email</span>
                <span className={member.email ? 'text-700' : 'text-500'}>{member.email || '—'}</span>
            </div>
            <div className="flex align-items-center gap-2">
                <i className="pi pi-phone text-500 w-1rem" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Phone</span>
                <span className={member.phone ? 'text-700' : 'text-500'}>{member.phone || '—'}</span>
            </div>
            <div className="flex align-items-center gap-2">
                <i className="pi pi-briefcase text-500 w-1rem" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Role / Headline</span>
                <span className={member.headline ? 'text-700' : 'text-500'}>{member.headline || '—'}</span>
            </div>
            <div className="flex align-items-center gap-2">
                <i className={`pi ${dateIcon} w-1rem`} aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Action Date</span>
                <span className={actionAt && status !== 'pending' ? 'text-700' : 'text-500'}>{dateText}</span>
            </div>
            <div className="flex align-items-start gap-2">
                <i className="pi pi-info-circle text-500 w-1rem mt-1" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem mt-1">Denial Reason</span>
                <span className={`line-height-3 ${member.lending_network_denied_reason ? 'text-600' : 'text-500'}`}>
                    {member.lending_network_denied_reason || '—'}
                </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-top-1 surface-border">
                {status !== 'approved' && (
                    <Button
                        label="Approve"
                        icon="pi pi-check"
                        severity="success"
                        outlined
                        size="small"
                        disabled={actioning}
                        onClick={() => onApprove(member)}
                    />
                )}
                {status !== 'denied' && (
                    <Button
                        label="Deny"
                        icon="pi pi-times"
                        severity="danger"
                        outlined
                        size="small"
                        disabled={actioning}
                        onClick={() => onDeny(member)}
                    />
                )}
                <Button
                    label="Remove"
                    icon="pi pi-trash"
                    severity="secondary"
                    outlined
                    size="small"
                    disabled={actioning}
                    onClick={() => onRemove(member)}
                />
            </div>
        </div>
    );
};

/**
 * Tenant Admin — Lending Network Management (v2.0)
 *
 * Route: /app/admin/lending-network
 *
 * Responsibilities:
 *   1. Toggle tenant-wide approval mode (auto / manual)
 *   2. View / filter members by status (All, Pending, Approved, Denied)
 *   3. Approve, Deny (with reason), Remove members
 */
const LendingNetworkAdmin = () => {
    const toast = useRef(null);

    // Settings
    const [approvalMode, setApprovalMode] = useState('auto');
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);

    // Cross-tenant visibility ceiling — auto-saved on change (P19)
    const [visibility, setVisibility] = useState({ profile_visibility: 'public', post_visibility: 'public' });
    const [visibilityOptions, setVisibilityOptions] = useState([]);
    const [loadingVisibility, setLoadingVisibility] = useState(true);
    const [savingVisibility, setSavingVisibility] = useState(false);

    // Members
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, denied: 0 });
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const limit = 10;

    // Deny dialog
    const [denyDialogVisible, setDenyDialogVisible] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [denyReason, setDenyReason] = useState('');
    const [actioning, setActioning] = useState(false);

    // Mobile accordion active tab — reset whenever result set changes so a
    // stale index doesn't open an unrelated row on a new page / filter / tab
    const [mobileActiveIndex, setMobileActiveIndex] = useState(null);
    useEffect(() => {
        setMobileActiveIndex(null);
    }, [activeTabIndex, debouncedSearch, page]);


    const tabStatuses = useMemo(() => ['all', 'pending', 'approved', 'denied'], []);

    const approvalModeOptions = useMemo(() => [
        { label: 'Auto Approve', value: 'auto' },
        { label: 'Manual Approval', value: 'manual' },
    ], []);

    const totalMembers = counts.pending + counts.approved + counts.denied;

    const tabMenuModel = useMemo(() => [
        { label: 'All' },
        { label: `Pending (${counts.pending})` },
        { label: `Approved (${counts.approved})` },
        { label: `Denied (${counts.denied})` },
    ], [counts]);

    // Debounced search (P13) — 500ms, resets page
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Load settings once
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await lendingNetworkService.getSettings();
                if (res.success) setApprovalMode(res.data.approval_mode);
            } catch (e) {
                console.error('Failed to load settings:', e?.message || 'Unknown error');
            } finally {
                setLoadingSettings(false);
            }
        };
        fetchSettings();
    }, []);

    // Load the cross-tenant visibility ceiling once
    useEffect(() => {
        const fetchVisibility = async () => {
            try {
                const res = await lendingNetworkService.getVisibility();
                if (res.success) {
                    setVisibility({
                        profile_visibility: res.data.profile_visibility,
                        post_visibility: res.data.post_visibility,
                    });
                    setVisibilityOptions(res.data.options || []);
                }
            } catch (e) {
                console.error('Failed to load visibility settings:', e?.message || 'Unknown error');
            } finally {
                setLoadingVisibility(false);
            }
        };
        fetchVisibility();
    }, []);

    /**
     * Auto-save a visibility change (P19): optimistic update, rollback on
     * failure. Both levels are sent together because the endpoint takes the
     * whole ceiling — sending one would blank the other.
     */
    const persistVisibility = useCallback(async (patch, successDetail) => {
        const previous = visibility;
        const next = { ...visibility, ...patch };
        setVisibility(next);
        setSavingVisibility(true);
        try {
            const res = await lendingNetworkService.updateVisibility(
                next.profile_visibility,
                next.post_visibility,
            );
            if (!res.success) throw new Error(res.message);
            toast.current?.show({ severity: 'success', summary: 'Saved', detail: successDetail, life: 2000 });
        } catch (e) {
            setVisibility(previous);
            toast.current?.show({
                severity: 'error',
                summary: 'Save failed',
                detail: e?.message || 'Could not save your change',
                life: 3500,
            });
        } finally {
            setSavingVisibility(false);
        }
    }, [visibility]);

    // Load members (P14 — primitive deps only)
    useEffect(() => {
        let cancelled = false;
        const fetchMembers = async () => {
            setLoading(true);
            try {
                const status = tabStatuses[activeTabIndex];
                const res = await lendingNetworkService.getMembers({
                    status,
                    search: debouncedSearch || undefined,
                    page,
                    limit,
                });
                if (cancelled) return;
                if (res.success) {
                    setMembers(res.data.members || []);
                    setTotalRecords(res.data.pagination?.total || 0);
                    setCounts(res.data.counts || { pending: 0, approved: 0, denied: 0 });
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('Failed to load members:', e?.message || 'Unknown error');
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load members' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchMembers();
        return () => { cancelled = true; };
    }, [activeTabIndex, debouncedSearch, page, tabStatuses]);

    // Re-fetch helper for post-action refresh
    const reloadMembers = useCallback(async () => {
        setLoading(true);
        try {
            const status = tabStatuses[activeTabIndex];
            const res = await lendingNetworkService.getMembers({
                status,
                search: debouncedSearch || undefined,
                page,
                limit,
            });
            if (res.success) {
                setMembers(res.data.members || []);
                setTotalRecords(res.data.pagination?.total || 0);
                setCounts(res.data.counts || { pending: 0, approved: 0, denied: 0 });
            }
        } catch (e) {
            console.error('Failed to reload members:', e?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [activeTabIndex, debouncedSearch, page, tabStatuses]);

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            const res = await lendingNetworkService.updateSettings(approvalMode);
            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Approval mode updated' });
            } else {
                throw new Error(res.message);
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to save settings' });
        } finally {
            setSavingSettings(false);
        }
    };

    const handleApprove = useCallback(async (profile) => {
        setActioning(true);
        try {
            const res = await lendingNetworkService.approveMember(profile.id);
            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Approved', detail: `${profile.full_name} approved` });
                reloadMembers();
            } else {
                throw new Error(res.message);
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to approve member' });
        } finally {
            setActioning(false);
        }
    }, [reloadMembers]);

    const openDenyDialog = useCallback((profile) => {
        setSelectedProfile(profile);
        setDenyReason('');
        setDenyDialogVisible(true);
    }, []);

    const handleDeny = async () => {
        if (!selectedProfile) return;
        setActioning(true);
        try {
            const res = await lendingNetworkService.denyMember(selectedProfile.id, denyReason.trim());
            if (res.success) {
                toast.current?.show({ severity: 'info', summary: 'Denied', detail: `${selectedProfile.full_name} denied` });
                setDenyDialogVisible(false);
                reloadMembers();
            } else {
                throw new Error(res.message);
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to deny member' });
        } finally {
            setActioning(false);
        }
    };

    const handleRemove = useCallback((profile) => {
        confirmDialog({
            message: `Remove ${profile.full_name} from the Lending Network?`,
            header: 'Confirm Remove',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const res = await lendingNetworkService.removeMember(profile.id);
                    if (res.success) {
                        toast.current?.show({ severity: 'warn', summary: 'Removed', detail: `${profile.full_name} removed` });
                        reloadMembers();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to remove member' });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to remove member' });
                }
            },
        });
    }, [reloadMembers]);

    // KPI card (P15). `accent` highlights the card with a left border when value > 0
    const KpiCard = ({ icon, label, value, description, accent }) => {
        const accentStyle = accent && value > 0
            ? { borderLeft: `3px solid var(--${accent}-500)` }
            : undefined;
        return (
            <div className="col-6 lg:col-3">
                <Card className="h-full shadow-1" style={accentStyle} pt={{ body: { className: 'p-3' } }}>
                    <div className="flex justify-content-between align-items-start">
                        <div className="flex flex-column gap-1">
                            <span className="text-600 text-sm font-medium">{label}</span>
                            <span className="text-900 text-xl font-semibold">{value}</span>
                        </div>
                        <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                            <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                        </div>
                    </div>
                    <span className="text-500 text-xs block mt-2">{description}</span>
                </Card>
            </div>
        );
    };

    // DataTable templates
    const memberTemplate = useCallback((rowData) => (
        <div className="flex align-items-center gap-2">
            <Avatar
                image={rowData.profile_photo_url_full}
                label={rowData.full_name?.charAt(0)?.toUpperCase()}
                shape="circle"
                size="normal"
                className="flex-shrink-0"
            />
            <div className="flex flex-column">
                <span className="font-medium text-sm text-900">{rowData.full_name || '—'}</span>
                <span className="text-xs text-500">{rowData.email || '—'}</span>
            </div>
        </div>
    ), []);

    const phoneTemplate = useCallback((rowData) => (
        <span className="text-sm text-700">{rowData.phone || '—'}</span>
    ), []);

    const headlineTemplate = useCallback((rowData) => (
        <span className="text-sm text-700">{rowData.headline || '—'}</span>
    ), []);

    const statusTemplate = useCallback((rowData) => {
        const severityMap = { pending: 'warning', approved: 'success', denied: 'danger' };
        const labelMap = { pending: 'Pending', approved: 'Approved', denied: 'Denied' };
        const status = rowData.lending_network_status;
        return <Tag value={labelMap[status] || status || '—'} severity={severityMap[status] || 'info'} />;
    }, []);

    const actionDateTemplate = useCallback((rowData) => {
        const { lending_network_status: status, lending_network_approved_at: actionAt } = rowData;
        if (status === 'pending' || !actionAt) return <span className="text-500 text-xs">—</span>;
        const d = new Date(actionAt);
        const icon = status === 'approved' ? 'pi-check-circle' : 'pi-times-circle';
        const iconColor = status === 'approved' ? 'text-green-600' : 'text-red-500';
        return (
            <div className="flex flex-column gap-1">
                <div className="flex align-items-center gap-1">
                    <i className={`pi ${icon} ${iconColor} text-xs`} aria-hidden="true" />
                    <span className="text-sm text-900">
                        {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                </div>
                <span className="text-xs text-500">
                    {d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </span>
            </div>
        );
    }, []);

    const denialReasonTemplate = useCallback((rowData) => (
        <span className="text-sm text-700">{rowData.lending_network_denied_reason || '—'}</span>
    ), []);

    const actionsTemplate = useCallback((rowData) => (
        <RowActionsMenu
            rowData={rowData}
            actioning={actioning}
            onApprove={handleApprove}
            onDeny={openDenyDialog}
            onRemove={handleRemove}
        />
    ), [actioning, handleApprove, openDenyDialog, handleRemove]);

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Page header (P6) */}
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Lending Network Management</h1>
                    <p className="text-600 text-sm m-0">Control who can join and participate in the Lending Network</p>
                </div>
            </div>

            {/* KPI cards (P15) */}
            <div className="grid mb-3">
                <KpiCard
                    icon="pi-users"
                    label="Total Members"
                    value={totalMembers}
                    description="Members across all statuses"
                />
                <KpiCard
                    icon="pi-clock"
                    label="Pending"
                    value={counts.pending}
                    description="Awaiting your approval"
                    accent="orange"
                />
                <KpiCard
                    icon="pi-check-circle"
                    label="Approved"
                    value={counts.approved}
                    description="Active in the network"
                />
                <KpiCard
                    icon="pi-ban"
                    label="Denied"
                    value={counts.denied}
                    description="Rejected members"
                />
            </div>

            {/* Approval Mode — collapsed by default (set-once configuration) */}
            <Accordion className="mb-3">
                <AccordionTab
                    header={
                        <span className="flex align-items-center gap-2 text-sm font-medium">
                            <i className="pi pi-cog text-primary" aria-hidden="true" />
                            <span>Approval Mode</span>
                            <Tag
                                value={approvalMode === 'auto' ? 'Auto Approve' : 'Manual Approval'}
                                severity={approvalMode === 'auto' ? 'success' : 'warning'}
                                className="text-xs ml-1"
                            />
                        </span>
                    }
                >
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3">
                        <div className="flex-1">
                            <p className="text-600 text-sm line-height-3 m-0">
                                <strong className="text-900">Auto:</strong> New profiles are approved immediately.{' '}
                                <strong className="text-900">Manual:</strong> New profiles start as &quot;Pending&quot; — you must approve each one.
                            </p>
                            <p className="text-500 text-xs m-0 mt-2">
                                Changing this only affects <strong>new</strong> profile creations. Existing approved profiles remain unchanged.
                            </p>
                        </div>
                        {loadingSettings ? (
                            <Skeleton width="14rem" height="2.5rem" />
                        ) : (
                            <div className="flex flex-wrap align-items-center gap-2">
                                <SelectButton
                                    value={approvalMode}
                                    onChange={(e) => { if (e.value) setApprovalMode(e.value); }}
                                    options={approvalModeOptions}
                                />
                                <Button
                                    label="Save"
                                    icon="pi pi-save"
                                    size="small"
                                    loading={savingSettings}
                                    onClick={handleSaveSettings}
                                />
                            </div>
                        )}
                    </div>
                </AccordionTab>
            </Accordion>

            {/*
              Cross-tenant visibility ceiling.

              A ceiling, not a per-user setting: it can only ever tighten what a
              company admin or a direct user chose for themselves, never loosen
              it. Auto-saves on change (P19) — there is nothing to batch, each
              switch is independent and safe on its own.
            */}
            <Accordion className="mb-3">
                <AccordionTab
                    header={
                        <span className="flex align-items-center gap-2 text-sm font-medium">
                            <i className="pi pi-eye text-primary" aria-hidden="true" />
                            <span>Visibility to Other Tenants</span>
                            <Tag
                                value={
                                    visibility.profile_visibility === 'public' && visibility.post_visibility === 'public'
                                        ? 'Open'
                                        : visibility.profile_visibility === 'tenant' && visibility.post_visibility === 'tenant'
                                            ? 'Closed'
                                            : 'Partial'
                                }
                                severity={
                                    visibility.profile_visibility === 'public' && visibility.post_visibility === 'public'
                                        ? 'info'
                                        : visibility.profile_visibility === 'tenant' && visibility.post_visibility === 'tenant'
                                            ? 'success'
                                            : 'warning'
                                }
                                className="text-xs ml-1"
                            />
                        </span>
                    }
                >
                    {loadingVisibility ? (
                        <div className="flex flex-column gap-3">
                            <Skeleton height="3rem" />
                            <Skeleton height="3rem" />
                        </div>
                    ) : (
                        <div className="flex flex-column gap-4">
                            <p className="text-600 text-sm line-height-3 m-0">
                                Decide whether people outside this tenant can see your members. This is a
                                ceiling — company admins and individual users can restrict further, but never
                                beyond what you allow here.
                            </p>

                            {/* Member profiles */}
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <i className="pi pi-id-card text-500" aria-hidden="true" />
                                        <span className="text-900 text-sm font-medium">Member Profiles</span>
                                    </div>
                                    <p className="text-500 text-xs m-0 line-height-3">
                                        Whether your members appear in network search and suggestions for users
                                        in other tenants.
                                    </p>
                                </div>
                                <SelectButton
                                    value={visibility.profile_visibility}
                                    options={visibilityOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    disabled={savingVisibility}
                                    onChange={(e) => {
                                        if (!e.value || e.value === visibility.profile_visibility) return;
                                        persistVisibility(
                                            { profile_visibility: e.value },
                                            e.value === 'public'
                                                ? 'Profiles are visible to other tenants'
                                                : 'Profiles stay inside this tenant',
                                        );
                                    }}
                                    aria-label="Profile visibility to other tenants"
                                />
                            </div>

                            {/* Member posts */}
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <i className="pi pi-comments text-500" aria-hidden="true" />
                                        <span className="text-900 text-sm font-medium">Member Posts</span>
                                    </div>
                                    <p className="text-500 text-xs m-0 line-height-3">
                                        Whether your members&apos; posts appear in the feed for users in other tenants.
                                    </p>
                                </div>
                                <SelectButton
                                    value={visibility.post_visibility}
                                    options={visibilityOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    disabled={savingVisibility}
                                    onChange={(e) => {
                                        if (!e.value || e.value === visibility.post_visibility) return;
                                        persistVisibility(
                                            { post_visibility: e.value },
                                            e.value === 'public'
                                                ? 'Posts are visible to other tenants'
                                                : 'Posts stay inside this tenant',
                                        );
                                    }}
                                    aria-label="Post visibility to other tenants"
                                />
                            </div>

                            <p className="text-500 text-xs m-0 line-height-3">
                                <i className="pi pi-info-circle mr-1" aria-hidden="true" />
                                Applies immediately to every existing member, not just new ones.
                            </p>
                        </div>
                    )}
                </AccordionTab>
            </Accordion>

            {/* Members */}
            <Card>
                {/* Filter bar (P7 — 1 filter pattern) */}
                <div className="surface-ground p-3 border-round mb-3">
                    <div className="grid">
                        <div className="col-12 md:col">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by name or email"
                                    maxLength={100}
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="col-12 md:col flex gap-2">
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                severity="secondary"
                                outlined
                                size="small"
                                className="flex-1 py-1 md:py-2"
                                onClick={() => setSearch('')}
                                disabled={!search}
                            />
                        </div>
                    </div>
                </div>

                {/* Status tab filter */}
                <TabMenu
                    model={tabMenuModel}
                    activeIndex={activeTabIndex}
                    onTabChange={(e) => { setActiveTabIndex(e.index); setPage(1); }}
                    className="mb-3"
                />

                {/* Mobile: compact card list (< md) */}
                <div className="block md:hidden">
                    {loading && (
                        <div className="flex flex-column gap-2">
                            <Skeleton height="110px" />
                            <Skeleton height="110px" />
                            <Skeleton height="110px" />
                        </div>
                    )}
                    {!loading && members.length === 0 && (
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                            <span className="text-600">No members found</span>
                        </div>
                    )}
                    {!loading && members.length > 0 && (
                        <>
                            <Accordion
                                activeIndex={mobileActiveIndex}
                                onTabChange={(e) => setMobileActiveIndex(e.index)}
                            >
                                {members.map((m, idx) => (
                                    <AccordionTab
                                        key={`member-${m.id || idx}`}
                                        header={<MemberHeader member={m} />}
                                    >
                                        <MemberDetails
                                            member={m}
                                            actioning={actioning}
                                            onApprove={handleApprove}
                                            onDeny={openDenyDialog}
                                            onRemove={handleRemove}
                                        />
                                    </AccordionTab>
                                ))}
                            </Accordion>
                            <Paginator
                                first={(page - 1) * limit}
                                rows={limit}
                                totalRecords={totalRecords}
                                onPageChange={(e) => setPage(Math.floor(e.first / limit) + 1)}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        </>
                    )}
                </div>

                {/* Desktop: DataTable with frozen Member + Actions (>= md) */}
                <div className="hidden md:block">
                    <DataTable
                        value={members}
                        dataKey="id"
                        loading={loading}
                        paginator
                        rows={limit}
                        totalRecords={totalRecords}
                        lazy
                        first={(page - 1) * limit}
                        onPage={(e) => setPage(Math.floor(e.first / limit) + 1)}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                        currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        responsiveLayout="scroll"
                        scrollable
                        stripedRows
                        showGridlines
                        size="small"
                        className="p-datatable-gridlines"
                        emptyMessage={
                            <div className="flex flex-column align-items-center py-5">
                                <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                <span className="text-600">No members found</span>
                            </div>
                        }
                    >
                        <Column
                            header="Member"
                            body={memberTemplate}
                            frozen
                            alignFrozen="left"
                            style={{ minWidth: '220px' }}
                        />
                        <Column field="phone" header="Phone" body={phoneTemplate} style={{ minWidth: '140px' }} />
                        <Column field="headline" header="Role / Headline" body={headlineTemplate} style={{ minWidth: '180px' }} />
                        <Column header="Status" body={statusTemplate} style={{ minWidth: '120px' }} />
                        <Column header="Action Date" body={actionDateTemplate} style={{ minWidth: '150px' }} />
                        <Column field="lending_network_denied_reason" header="Denial Reason" body={denialReasonTemplate} style={{ minWidth: '180px' }} />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            frozen
                            alignFrozen="right"
                            style={{ minWidth: '70px', width: '70px' }}
                        />
                    </DataTable>
                </div>
            </Card>

            {/* Deny dialog (P4 mobile-safe) */}
            <Dialog
                visible={denyDialogVisible}
                onHide={() => setDenyDialogVisible(false)}
                header={`Deny: ${selectedProfile?.full_name || selectedProfile?.email || 'Member'}`}
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '450px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={() => setDenyDialogVisible(false)}
                        />
                        <Button
                            label="Deny Member"
                            icon="pi pi-times"
                            severity="danger"
                            size="small"
                            loading={actioning}
                            onClick={handleDeny}
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="deny-reason" className="block font-medium text-sm mb-2">
                        Reason <span className="text-500 font-normal">(optional)</span>
                    </label>
                    <InputTextarea
                        id="deny-reason"
                        value={denyReason}
                        onChange={(e) => setDenyReason(e.target.value)}
                        rows={3}
                        maxLength={500}
                        className="w-full"
                        placeholder="Explain why this member is being denied..."
                    />
                    <div className="flex justify-content-between mt-1">
                        <small className="text-500">Visible to the user on the Lending Network page.</small>
                        <small className="text-500">{denyReason.length}/500</small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default LendingNetworkAdmin;
